import { useState, useEffect, useRef, FormEvent } from 'react';
import { useWhatsAppLead } from '@/hooks/useWhatsAppLead';
import { supabase } from '@/lib/supabase';
import { trackEvent } from '@/hooks/useTracking';

function getSessionId(): string {
  const key = 'shp_session_id';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(key, id);
  }
  return id;
}

const WHATSAPP_NUMBER = '908508408383';

const ACTION_OPTIONS = [
  'Randevu almak istiyorum',
  'Siteden satış yapmak istiyorum',
  'Müşterileri görüşmeye çağırmak istiyorum',
  'Lead / form toplamak istiyorum',
  'Marka bilinirliği oluşturmak istiyorum',
  'Diğer',
];

const SECTOR_OPTIONS = [
  'Güzellik & Bakım (Kuaför, Güzellik Merkezi, Nail Art...)',
  'Sağlık & Klinik (Diş, Psikoloji, Diyetisyen...)',
  'Spor & Fitness (Pilates, Gym, Yoga...)',
  'Hukuk & Danışmanlık',
  'Eğitim & Kurs',
  'Oto Servis & Teknik',
  'Restoran & Kafe',
  'E-ticaret & Perakende',
  'Fotoğrafçılık & Kreatif',
  'Diğer',
];

interface FormState {
  firstName: string;
  lastName: string;
  company: string;
  sector: string;
  sectorCustom: string;
  monthlyTarget: string;
  targetAudience: string;
  actionType: string;
  actionCustom: string;
  notes: string;
}

const INITIAL_FORM: FormState = {
  firstName: '',
  lastName: '',
  company: '',
  sector: '',
  sectorCustom: '',
  monthlyTarget: '',
  targetAudience: '',
  actionType: '',
  actionCustom: '',
  notes: '',
};

export default function WhatsAppLeadModal() {
  const { isOpen, closeModal, leadContext } = useWhatsAppLead();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [step, setStep] = useState<1 | 2>(1);
  const overlayRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setForm(INITIAL_FORM);
      setErrors({});
      setStep(1);
      document.body.style.overflow = 'hidden';
      setTimeout(() => firstInputRef.current?.focus(), 100);
      trackEvent('modal_open', 'WhatsApp Modal Açıldı', 'Modal', { lead_context: leadContext });
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, leadContext]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    if (isOpen) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, closeModal]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) closeModal();
  };

  const set = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateStep1 = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};
    if (!form.firstName.trim()) newErrors.firstName = 'Ad gerekli';
    if (!form.lastName.trim()) newErrors.lastName = 'Soyad gerekli';
    if (!form.sector) newErrors.sector = 'Sektör seçiniz';
    if (form.sector === 'Diğer' && !form.sectorCustom.trim()) newErrors.sectorCustom = 'Sektörünüzü yazınız';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};
    if (!form.actionType) newErrors.actionType = 'Hedef aksiyonu seçiniz';
    if (form.actionType === 'Diğer' && !form.actionCustom.trim()) newErrors.actionCustom = 'Hedefinizi yazınız';
    if (!form.targetAudience.trim()) newErrors.targetAudience = 'Hedef kitlenizi belirtiniz';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
      trackEvent('modal_step2', 'Modal Adım 2\'ye Geçti', 'Modal', { sector: form.sector });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateStep2()) return;

    const sectorLabel = form.sector === 'Diğer' ? form.sectorCustom : form.sector;
    const actionLabel = form.actionType === 'Diğer' ? form.actionCustom : form.actionType;

    // Track form submit
    trackEvent('whatsapp_submit', 'WhatsApp Formu Gönderildi', 'Modal', {
      sector: sectorLabel,
      action_type: actionLabel,
      lead_context: leadContext,
    });

    // Save lead to Supabase — session_id ile tracking'e bağla
    await supabase.from('leads').insert({
      first_name: form.firstName,
      last_name: form.lastName,
      company: form.company || null,
      sector: sectorLabel,
      monthly_target: form.monthlyTarget || null,
      target_audience: form.targetAudience,
      action_type: actionLabel,
      notes: form.notes || null,
      lead_context: leadContext || null,
      session_id: getSessionId(),
    });

    const lines = [
      `Merhaba, Smart Hizmet platformu hakkında bilgi almak istiyorum.`,
      ``,
      `👤 *Ad Soyad:* ${form.firstName} ${form.lastName}`,
      form.company ? `🏢 *Firma:* ${form.company}` : null,
      `🏷️ *Sektör:* ${sectorLabel}`,
      form.monthlyTarget ? `💰 *Aylık Kazanç Hedefi:* ${form.monthlyTarget}` : null,
      `🎯 *Hedef Kitle:* ${form.targetAudience}`,
      `⚡ *Ulaşmak İstediğim Hedef:* ${actionLabel}`,
      form.notes ? `📝 *Ek Notlar:* ${form.notes}` : null,
      leadContext ? `📌 *İlgi Alanı:* ${leadContext}` : null,
    ].filter(Boolean).join('\n');

    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    closeModal();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={handleOverlayClick}
    >
      <div
        className="relative w-full max-w-lg rounded-3xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
          border: '1px solid rgba(240,180,41,0.25)',
          boxShadow: '0 0 60px rgba(240,180,41,0.12), 0 30px 80px rgba(0,0,0,0.6)',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 border-b border-white/8"
          style={{ background: 'rgba(15,15,26,0.95)', backdropFilter: 'blur(12px)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-2xl bg-green-500/15 border border-green-500/30">
              <i className="ri-whatsapp-line text-green-400 text-xl" />
            </div>
            <div>
              <h3 className="text-white font-extrabold text-base leading-tight">WhatsApp&apos;tan Ulaş</h3>
              <p className="text-gray-500 text-xs mt-0.5">Bilgilerini paylaş, sana özel yanıt verelim</p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-3 px-6 pt-5 pb-1">
          {[1, 2].map((s) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all duration-300 flex-shrink-0"
                style={{
                  background: step >= s ? 'linear-gradient(135deg, #f0b429, #e67e22)' : 'rgba(255,255,255,0.06)',
                  color: step >= s ? '#000' : '#666',
                  border: step >= s ? 'none' : '1px solid rgba(255,255,255,0.1)',
                }}
              >
                {step > s ? <i className="ri-check-line text-xs" /> : s}
              </div>
              <span className={`text-xs font-semibold ${step >= s ? 'text-gold' : 'text-gray-600'}`}>
                {s === 1 ? 'Bilgileriniz' : 'Hedefiniz'}
              </span>
              {s < 2 && (
                <div className="flex-1 h-px mx-1" style={{ background: step > s ? 'rgba(240,180,41,0.4)' : 'rgba(255,255,255,0.06)' }} />
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-6 pt-4">
          {step === 1 && (
            <div className="space-y-4">
              {/* Ad / Soyad */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">Ad <span className="text-gold">*</span></label>
                  <input
                    ref={firstInputRef}
                    type="text"
                    value={form.firstName}
                    onChange={set('firstName')}
                    placeholder="Adınız"
                    className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: errors.firstName ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(255,255,255,0.1)',
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(240,180,41,0.5)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = errors.firstName ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.1)'; }}
                  />
                  {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">Soyad <span className="text-gold">*</span></label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={set('lastName')}
                    placeholder="Soyadınız"
                    className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: errors.lastName ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(255,255,255,0.1)',
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(240,180,41,0.5)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = errors.lastName ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.1)'; }}
                  />
                  {errors.lastName && <p className="text-red-400 text-xs mt-1">{errors.lastName}</p>}
                </div>
              </div>

              {/* Firma */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5">Firma / İşletme Adı</label>
                <input
                  type="text"
                  value={form.company}
                  onChange={set('company')}
                  placeholder="Firma adınız (opsiyonel)"
                  className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(240,180,41,0.5)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                />
              </div>

              {/* Sektör */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5">Sektörünüz <span className="text-gold">*</span></label>
                <select
                  value={form.sector}
                  onChange={set('sector')}
                  className="w-full rounded-xl px-4 py-3 text-sm text-white outline-none transition-all cursor-pointer appearance-none"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: errors.sector ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(255,255,255,0.1)',
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(240,180,41,0.5)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = errors.sector ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.1)'; }}
                >
                  <option value="" style={{ background: '#1a1a2e' }}>Sektör seçiniz...</option>
                  {SECTOR_OPTIONS.map((s) => (
                    <option key={s} value={s} style={{ background: '#1a1a2e' }}>{s}</option>
                  ))}
                </select>
                {errors.sector && <p className="text-red-400 text-xs mt-1">{errors.sector}</p>}
              </div>

              {form.sector === 'Diğer' && (
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">Sektörünüzü belirtin <span className="text-gold">*</span></label>
                  <input
                    type="text"
                    value={form.sectorCustom}
                    onChange={set('sectorCustom')}
                    placeholder="Sektörünüzü yazınız"
                    className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: errors.sectorCustom ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(255,255,255,0.1)',
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(240,180,41,0.5)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = errors.sectorCustom ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.1)'; }}
                  />
                  {errors.sectorCustom && <p className="text-red-400 text-xs mt-1">{errors.sectorCustom}</p>}
                </div>
              )}

              <button
                type="button"
                onClick={handleNext}
                className="w-full btn-gold py-3.5 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 mt-2"
              >
                Devam Et
                <i className="ri-arrow-right-line" />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              {/* Aylık kazanç hedefi */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5">Aylık Kazanç Hedefiniz</label>
                <input
                  type="text"
                  value={form.monthlyTarget}
                  onChange={set('monthlyTarget')}
                  placeholder="Örn: 50.000 TL, 100.000 TL..."
                  className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(240,180,41,0.5)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                />
              </div>

              {/* Hedef kitle */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5">Ulaşmak İstediğiniz Kitle <span className="text-gold">*</span></label>
                <input
                  type="text"
                  value={form.targetAudience}
                  onChange={set('targetAudience')}
                  placeholder="Örn: 25-45 yaş arası kadınlar, İstanbul'daki işletmeler..."
                  className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: errors.targetAudience ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(255,255,255,0.1)',
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(240,180,41,0.5)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = errors.targetAudience ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.1)'; }}
                />
                {errors.targetAudience && <p className="text-red-400 text-xs mt-1">{errors.targetAudience}</p>}
              </div>

              {/* Hedef aksiyon */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5">Ulaşmak İstediğiniz Hedef <span className="text-gold">*</span></label>
                <div className="grid grid-cols-1 gap-2">
                  {ACTION_OPTIONS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        setForm((prev) => ({ ...prev, actionType: opt }));
                        if (errors.actionType) setErrors((prev) => ({ ...prev, actionType: '' }));
                      }}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-left transition-all cursor-pointer"
                      style={{
                        background: form.actionType === opt ? 'rgba(240,180,41,0.12)' : 'rgba(255,255,255,0.04)',
                        border: form.actionType === opt ? '1px solid rgba(240,180,41,0.45)' : '1px solid rgba(255,255,255,0.08)',
                        color: form.actionType === opt ? '#f0b429' : '#9ca3af',
                      }}
                    >
                      <span
                        className="w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center"
                        style={{
                          borderColor: form.actionType === opt ? '#f0b429' : 'rgba(255,255,255,0.2)',
                          background: form.actionType === opt ? '#f0b429' : 'transparent',
                        }}
                      >
                        {form.actionType === opt && <span className="w-1.5 h-1.5 rounded-full bg-black" />}
                      </span>
                      {opt}
                    </button>
                  ))}
                </div>
                {errors.actionType && <p className="text-red-400 text-xs mt-1">{errors.actionType}</p>}
              </div>

              {form.actionType === 'Diğer' && (
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">Hedefinizi belirtin <span className="text-gold">*</span></label>
                  <input
                    type="text"
                    value={form.actionCustom}
                    onChange={set('actionCustom')}
                    placeholder="Hedefinizi yazınız"
                    className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: errors.actionCustom ? '1px solid rgba(239,68,68,0.6)' : '1px solid rgba(255,255,255,0.1)',
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(240,180,41,0.5)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = errors.actionCustom ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.1)'; }}
                  />
                  {errors.actionCustom && <p className="text-red-400 text-xs mt-1">{errors.actionCustom}</p>}
                </div>
              )}

              {/* Ek notlar */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1.5">Eklemek İstedikleriniz</label>
                <textarea
                  value={form.notes}
                  onChange={set('notes')}
                  placeholder="Mevcut durumunuz, beklentileriniz veya sormak istedikleriniz..."
                  rows={3}
                  maxLength={500}
                  className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition-all resize-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(240,180,41,0.5)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                />
                <p className="text-gray-600 text-xs mt-1 text-right">{form.notes.length}/500</p>
              </div>

              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-shrink-0 px-5 py-3.5 rounded-xl font-semibold text-sm cursor-pointer whitespace-nowrap flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <i className="ri-arrow-left-line" />
                  Geri
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-gold py-3.5 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-whatsapp-line text-base" />
                  WhatsApp&apos;ta Gönder
                </button>
              </div>

              <p className="text-center text-gray-600 text-xs">
                Bilgileriniz yalnızca size özel teklif hazırlamak için kullanılır.
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
